<template>
  <div class="modal-overlay" v-if="showOtpModal" @click.self="close">
    <div class="modal-content">
      <button class="close-btn" @click="close">&times;</button>

      <div class="modal-header">
        <h2><span class="sports">SPORTS</span><span class="hub">HUB</span></h2>
        <h3>Welcome to Sports Hub</h3>
        <p class="subtitle">{{ otpMessage || 'Please enter the OTP sent via SMS to enjoy Sports Hub.' }}</p>
      </div>

      <div class="modal-body">
        <div class="input-group">
          <label for="otp-input">Enter Your OTP</label>
          <input
            id="otp-input"
            type="text"
            v-model="otp"
            placeholder="••••••"
            maxlength="6"
            inputmode="numeric"
            autocomplete="one-time-code"
            @keyup.enter="submit"
          />
        </div>

        <div class="action-buttons">
          <button class="submit-btn" @click="submit" :disabled="isLoading">
            {{ isLoading ? 'Verifying...' : 'Submit' }}
          </button>
          <button class="resend-btn" @click="resend" :disabled="isLoading || resendCooldown > 0">
            {{ resendCooldown > 0 ? `Request new code in ${resendCooldown}s` : 'Request new code' }}
          </button>
        </div>

        <p class="terms">
          By continuing you agree to the <a href="#" target="_blank" rel="noopener">terms & conditions</a>. Data charges may apply.
        </p>

        <p class="error" v-if="error">{{ error }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import DcbService from '../services/dcbService'
import modalMixin from '@/mixins/modalMixin'

export default {
  name: 'OtpModal',
  mixins: [modalMixin],
  data() {
    return {
      otp: '',
      resendCooldown: 0,
      cooldownInterval: null
    }
  },
  computed: {
    ...mapState(['showOtpModal', 'isLoading', 'error', 'msisdn', 'otpMessage', 'brandConfig', 'pendingAction', 'intendedPath', 'serviceId'])
  },
  methods: {
    ...mapMutations(['closeAllModals', 'setIsLoading', 'setError', 'setSessionId', 'setPendingAction', 'setOtpMessage', 'setIntendedPath']),
    ...mapActions(['fetchAndSetUser']),

    async submit() {
      if (this.otp.length < 4) return

      this.setIsLoading(true)
      this.setError(null)

      const serviceId = this.serviceId || this.brandConfig?.serviceId
      const result = await DcbService.validateOtp(this.msisdn, serviceId, this.otp)
      this.setIsLoading(false)

      if (result.success) {
        this.setSessionId(result.sessionId)
        await this.fetchAndSetUser({ force: true })

        // Tealium: fresh login (OTP). Non-blocking; analytics must not affect UX.
        import('@/services/tealium')
          .then((m) => m.postTealiumLogin())
          .catch(() => {})

        this.closeAllModals()

        if (this.pendingAction) {
          const action = this.pendingAction
          this.setPendingAction(null)
          if (typeof action === 'function') action()
        }
        if (this.intendedPath) {
          const path = this.intendedPath
          this.setIntendedPath(null)
          this.$router.push(path).catch(() => {})
        }
      } else {
        this.setError(result.error)
      }
    },

    async resend() {
      if (this.resendCooldown > 0) return

      this.setIsLoading(true)
      this.setError(null)

      const serviceId = this.serviceId || this.brandConfig?.serviceId
      const result = await DcbService.requestOtp(this.msisdn, serviceId)
      this.setIsLoading(false)

      if (result.success) {
        this.setOtpMessage(result.message)
        this.startResendCooldown()
      } else {
        this.setError(result.message)
      }
    },

    startResendCooldown() {
      const COOLDOWN_SECONDS = 60
      this.resendCooldown = COOLDOWN_SECONDS

      if (this.cooldownInterval) clearInterval(this.cooldownInterval)
      this.cooldownInterval = setInterval(() => {
        this.resendCooldown -= 1
        if (this.resendCooldown <= 0) {
          clearInterval(this.cooldownInterval)
          this.cooldownInterval = null
        }
      }, 1000)
    }
  },
  beforeUnmount() {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval)
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/modal-common';

.subtitle {
  font-size: 14px;
  color: $gray-600;
  margin: 0;
}

.modal-body {
  .input-group {
    margin-bottom: $space-lg;

    label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: $gray-700;
      margin-bottom: $space-sm;
    }

    input {
      text-align: center;
      letter-spacing: 10px;
      font-size: 20px;
      font-weight: 600;
      margin-bottom: 0;
    }
  }

  .action-buttons {
    display: flex;
    flex-direction: column;
    gap: $space-sm;
    margin-bottom: $space-lg;
  }

  .submit-btn {
    @include primary-btn;
    padding: 14px 20px;
  }

  .resend-btn {
    width: 100%;
    padding: 12px 20px;
    background: transparent;
    border: 1.5px solid $gray-300;
    border-radius: $radius-sm;
    font-size: 14px;
    font-weight: 600;
    color: $gray-700;
    cursor: pointer;
    transition: all $transition-base;

    &:hover:not(:disabled) {
      border-color: $brand-red;
      color: $brand-red;
      background: rgba($brand-red, 0.04);
    }

    &:active:not(:disabled) {
      transform: scale(0.98);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .terms {
    font-size: 12px;
    color: $gray-500;
    line-height: 1.5;
    margin: 0;
    text-align: center;

    a {
      color: $brand-red;
      text-decoration: none;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .error {
    text-align: center;
    margin-top: $space-sm;
  }
}
</style>
